import { useCallback } from 'react';

export function useSafeWindowOpen() {
  /**
   * Safely open a URL in a new or same tab across all browsers, including iOS Safari.
   *
   * @param url - The URL to open
   * @param opts - Optional { immediate?: boolean, features?: string, target?: '_blank' | '_self' }
   */
  return useCallback(
    (
      url: string,
      opts?: {
        immediate?: boolean;
        features?: string;
        target?: '_blank' | '_self';
      }
    ) => {
      const {
        immediate = false,
        features = 'noopener,noreferrer',
        target = '_blank',
      } = opts || {};

      // If opening in the same tab, just replace location
      if (target === '_self') {
        if (immediate) {
          window.location.href = url;
          return null;
        }

        // Add small delay if not immediate (simulate async)
        setTimeout(() => {
          window.location.href = url;
        }, 10);

        return null;
      }

      // Otherwise, handle _blank (new tab) behavior
      if (immediate) {
        const win = window.open(url, '_blank', features);
        if (win) win.opener = null;
        return win;
      }

      const newTab = window.open('', '_blank', features);
      if (!newTab) return null; // popup blocked

      newTab.document.write('<p>Loading...</p>');
      setTimeout(() => {
        newTab.location.href = url;
      }, 10);

      return newTab;
    },
    []
  );
}
